import { createElement } from 'react';

// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import { v4 as uuid } from 'uuid';

import type { PortalProviderAPI } from '@atlaskit/editor-common/common';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { Decoration, type DecorationSet } from '@atlaskit/editor-prosemirror/view';

import {
	CURSOR_DROP_INDICATOR_GAP,
	CURSOR_DROP_INDICATOR_OFFSET,
	CursorDropIndicator,
} from '../ui/cursor-drop-indicator';

export const CURSOR_INDICATOR_DEC_TYPE = 'hub-external-insert-cursor';

export function findCursorIndicatorDecs(decorations: DecorationSet): Decoration[] {
	return decorations.find(0, 1e9, (spec) => spec.type === CURSOR_INDICATOR_DEC_TYPE);
}

/** Minimal gap/offset for cursor indicator alignment (first version – constants). */
function getGapAndOffset(
	_prevNode?: PMNode | null,
	_nextNode?: PMNode | null,
	_parentNode?: PMNode | null,
): { gap: number; offset: number } {
	if (!_prevNode && _nextNode) {
		return { gap: 0, offset: 0 };
	}
	if (_prevNode && !_nextNode) {
		return { gap: 0, offset: 0 };
	}
	return { gap: 4, offset: 0 };
}

/**
 * Creates a single widget decoration for the cursor drop indicator at
 * the given position. Renders {@link CursorDropIndicator} via the
 * editor portal.
 *
 * The widget host is `position: relative` and `width: 100%` so its
 * absolutely-positioned child indicator stretches to the **natural
 * width of the container ProseMirror placed the widget into** —
 * top-level content width at the document root, the column width
 * inside a `layoutColumn`, the frame width inside a hub `section`
 * (`extensionFrame`), and so on. No `lineLength` / fixed pixel width
 * is needed because each container already constrains its own width.
 */
export function createCursorIndicatorDecoration(
	pos: number,
	nodeViewPortalProviderAPI: PortalProviderAPI,
	prevNode?: PMNode | null,
	nextNode?: PMNode | null,
	parentNode?: PMNode | null,
): Decoration {
	// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
	const key = `hub-external-insert-cursor-${uuid()}`;
	const { gap, offset } = getGapAndOffset(prevNode, nextNode, parentNode);

	return Decoration.widget(
		pos,
		() => {
			const element = document.createElement('div');
			element.setAttribute('data-hub-external-insert-cursor', 'true');
			element.style.clear = 'unset';
			element.style.display = 'block';
			// `position: relative` makes this host the offset parent of
			// the indicator's `position: absolute` container, so the
			// indicator's `left: 0; right: 0` resolves against this
			// host's natural (container-constrained) width.
			element.style.position = 'relative';
			element.style.width = '100%';
			element.style.setProperty(CURSOR_DROP_INDICATOR_OFFSET, `${offset}px`);
			element.style.setProperty(CURSOR_DROP_INDICATOR_GAP, `${gap}px`);

			try {
				nodeViewPortalProviderAPI.render(
					() => createElement(CursorDropIndicator),
					element,
					key,
					undefined,
					true,
				);
			} catch {}

			return element;
		},
		{
			type: CURSOR_INDICATOR_DEC_TYPE,
			destroy: () => {
				nodeViewPortalProviderAPI.remove(key);
			},
		},
	);
}
